import { get, map, pick, groupBy } from "lodash"
import { INSTRUMENTS, INSTRUMENT_TYPES } from "connected-components/dashboard/helpers"
import { DELTA_SYMBOL } from "helpers/assets/constants"
import { camelCaseToSpace } from "helpers/string"
import { getNMVTooltipComponent } from "helpers/assets/tooltips"
import { VALUE_FORMATS, ASSET_METRIC_IDS } from "./constants"
import { CurrencyScaledLabel } from "./currency-scale-label"

export function makeDataPath(dataPath, suffix = "") {
  if (!dataPath) {
    return null
  }
  const dataPathSteps = dataPath.split(".")
  dataPathSteps[0] = `${dataPathSteps[0]}${suffix}`
  return dataPathSteps.join(".")
}

export function getMetricSignClass(rawValue) {
  const numericValue = Number(rawValue)

  if (!Number.isFinite(numericValue) || numericValue === 0) {
    return ""
  }

  return numericValue < 0 ? "negative" : "positive"
}

const COMPOSITION_DESCRIPTION_BUCKET_ORDER = [
  INSTRUMENT_TYPES.original,
  INSTRUMENT_TYPES.rebalanced,
  INSTRUMENT_TYPES.comparison,
]

export function getFirstAvailableCompositionDescription(values) {
  if (!values || typeof values !== "object") {
    return null
  }
  for (const instrumentType of COMPOSITION_DESCRIPTION_BUCKET_ORDER) {
    const path = makeDataPath("composition.description", INSTRUMENTS[instrumentType].metricsSuffix)
    const part = get(values, path)
    if (part != null && part !== "") {
      return part
    }
  }
  return null
}

export function makeDataGetter(dataPath, instrumentColumns) {
  if (!dataPath) {
    return null
  }
  const currentInstrumentDataPath = dataPath.split(".")
  const newInstrumentDataPath = [...currentInstrumentDataPath]
  const currentInstrumentSuffix = INSTRUMENTS[instrumentColumns[0]].metricsSuffix
  const newInstrumentSuffix = INSTRUMENTS[instrumentColumns[1]].metricsSuffix
  currentInstrumentDataPath[0] = `${currentInstrumentDataPath[0]}${currentInstrumentSuffix}`
  newInstrumentDataPath[0] = `${newInstrumentDataPath[0]}${newInstrumentSuffix}`
  return (item) => {
    const currentInstrumentValue = get(item, currentInstrumentDataPath)
    const newInstrumentValue = get(item, newInstrumentDataPath)
    if (currentInstrumentValue == null && newInstrumentValue == null) {
      return null
    }
    return (currentInstrumentValue || 0) - (newInstrumentValue || 0)
  }
}

export function getInstrumentTypeCharacter(instrumentType, isEndVsStartDateComparison, isCompareWeightStrategy, isOptimization) {
  const instrumentTypeCharacters = {
    original: "O",
    rebalanced: "R",
    comparison: isOptimization ? "T" : "C",
    delta: DELTA_SYMBOL,
  }

  if (isCompareWeightStrategy) {
    instrumentTypeCharacters.comparison = "Sel"
    instrumentTypeCharacters.delta = "Siz"
  }

  if (isEndVsStartDateComparison) {
    instrumentTypeCharacters.original = "E"
    instrumentTypeCharacters.comparison = "S"
  }

  return instrumentTypeCharacters[instrumentType]
}

export function makeLabel(label, instrumentType, isEndVsStartDateComparison, isCompareWeightStrategy, isOptimization) {
  const instrumentTypeCharacter = getInstrumentTypeCharacter(instrumentType, isEndVsStartDateComparison, isCompareWeightStrategy, isOptimization)
  const instrumentTypePrefix = instrumentTypeCharacter ? `${instrumentTypeCharacter}: ` : ""
  return `${instrumentTypePrefix}${label}`
}

export function isInstrumentWithoutGMV(instrumentType) {
  return ["INDEX", "ETF"].includes(instrumentType)
}

// Net MV (marketValue) has no GMV for index-style originals, so it's hidden whenever the
// original/rebalanced column would render it. Shared by the default metric guards and the
// composition-prepend path so the rule can't drift between them.
/**
 * @param {string[]} [instrumentColumns]
 * @param {{ type?: string } | null} [originalInstrument]
 */
export function shouldHideMarketValueColumn(instrumentColumns = [], originalInstrument = null) {
  return instrumentColumns.some((column) => ["original", "rebalanced"].includes(column))
    && isInstrumentWithoutGMV(originalInstrument?.type)
}

export function filterColumnsBasedOnGMV(instrumentColumns, originalInstrument, rebalancedInstrument, comparisonInstrument) {
  const instrumentTypes = {
    original: originalInstrument?.type,
    // Rebalanced instrument is considered of the same type as the original one for this purpose
    rebalanced: originalInstrument?.type,
    comparison: comparisonInstrument?.type,
  }

  if (instrumentColumns.length > 1 && isInstrumentWithoutGMV(instrumentTypes[instrumentColumns[1]])) {
    return [instrumentColumns[0]]
  }

  return instrumentColumns
}

export function reorderByCategory(items) {
  const categoryOrder = Array.from(
    new Set(items.map(item => item.category)),
  )

  const groupedByCategory = categoryOrder.reduce((acc, category) => {
    acc[category] = items.filter(item => item.category === category)
    return acc
  }, {})

  return categoryOrder.flatMap(category => groupedByCategory[category])
}

const defaultMetricOptions = [{
  id: "value",
  isDefault: true,
  format: VALUE_FORMATS.NUMBER,
  dataPath: ({ prefix, factorId, dataPath }) => {
    if (dataPath) {
      return dataPath
    }
    if (prefix) {
      return `${prefix}.${factorId}`
    }
    return factorId
  },
}]

export function createMetricGroup(prefix = "", metadata, { name = null, metricOptions = defaultMetricOptions, canAggregateCategories = false, modelId } = {}) {
  if (!metadata) {
    return null
  }
  const { categories, factors } = metadata

  const categoryMap = categories.reduce((map, cat) => {
    map[cat.id] = cat
    return map
  }, {})

  const categoryGroups = factors.reduce((acc, factor) => {
    const categoryId = factor.category

    if (!acc[categoryId]) {
      const category = categoryMap[categoryId] || { id: categoryId, label: categoryId }
      acc[categoryId] = {
        ...category,
        id: categoryId,
        modelId,
        label: category.name || category.label,
        singleFactor: category.singleFactor,
        items: [],
      }
    }

    acc[categoryId].items.push({
      id: factor.id,
      modelId,
      label: factor.name || factor.label,
      category: categoryId,
      metricTypeOptions: createMetricTypeOptions(metricOptions, { prefix, factorId: factor.id, modelId }),
    })

    return acc
  }, {})

  if (canAggregateCategories) {
    const aggregateCategory = {
      id: "factorAggregates",
      label: "Category Aggregates",
      items: [],
      modelId,
    }
    categoryGroups[aggregateCategory.id] = aggregateCategory
    categoryGroups[aggregateCategory.id].items = categories.map(category => ({
      id: category.id,
      modelId,
      label: category.label,
      metricTypeOptions: createMetricTypeOptions(metricOptions, { prefix, factorId: category.id, modelId }),
    }))
  }

  return {
    id: prefix,
    label: name || metadata.name,
    modelId,
    items: Object.values(categoryGroups),
    isContentSet: "proprietary" in metadata,
    ...pick(metadata, ["categories", "proprietary"]),
  }
}

export function createMetricTypeOptions(metricOptions, options = {}) {
  return map(metricOptions, metricOption => {
    const fallbackDataPath = options.prefix ? `${options.prefix}.${options.factorId}.${metricOption.id}` : `${options.factorId}.${metricOption.id}`
    return {
      ...metricOption,
      modelId: options.modelId,
      format: options.format || metricOption.format,
      prefix: options.prefix,
      dataPath: typeof metricOption.dataPath === "function" ? metricOption.dataPath(options) : fallbackDataPath,
    }
  })
}

export function hasAction(action, instrumentType, position, instrumentColumns) {
  if (!action || position.id === "notheld" || instrumentType === "comparison") {
    return false
  }
  if (instrumentType !== "delta") {
    return true
  }
  if (instrumentColumns.includes("comparison")) {
    return false
  }
  return true
}

export function makeMetricLabel(metric, instrumentType, instrumentColumns, isEndVsStartDateComparison, isCompareWeightStrategy, isOptimization) {
  if (instrumentColumns.length === 1 || metric.onlyShowOnceInDownload || metric.hideInstrumentName) {
    return metric.label
  }
  return makeLabel(metric.label, instrumentType, isEndVsStartDateComparison, isCompareWeightStrategy, isOptimization)
}

export function makeMetadataGetter(baseDataPath) {
  return (values) => {
    const result = Object.entries(INSTRUMENTS).find(([instrumentId, instrument]) => {
      if (instrumentId === "delta") return false
      const path = makeDataPath(baseDataPath, instrument.metricsSuffix)
      const instrumentValue = get(values, path)
      return !!instrumentValue
    })
    return result ? get(values, makeDataPath(baseDataPath, result[1].metricsSuffix)) : null
  }
}

export function getDefaultDownloadMetrics(instrumentType) {
  const prefix = `composition${INSTRUMENTS[instrumentType].metricsSuffix}`
  return [
    {
      id: "identifier",
      label: "Identifier",
      format: VALUE_FORMATS.STRING,
      dataGetter: makeMetadataGetter("composition.identifier"),
      dataPath: `${prefix}.identifier`,
      downloadOnly: true,
      onlyShowOnceInDownload: true,
    }, {
      id: "mic",
      label: "MIC",
      format: VALUE_FORMATS.STRING,
      dataGetter: makeMetadataGetter("composition.mic"),
      dataPath: `${prefix}.mic`,
      downloadOnly: true,
      onlyShowOnceInDownload: true,
    }, {
      id: "bloombergTicker",
      label: "Bloomberg Ticker",
      format: VALUE_FORMATS.STRING,
      dataGetter: makeMetadataGetter("composition.bloombergTicker"),
      dataPath: `${prefix}.bloombergTicker`,
      downloadOnly: true,
      onlyShowOnceInDownload: true,
    }, {
      id: "country",
      label: "Country",
      format: VALUE_FORMATS.STRING,
      dataGetter: makeMetadataGetter("composition.country"),
      dataPath: `${prefix}.country`,
      downloadOnly: true,
      onlyShowOnceInDownload: true,
    }, {
      id: "assetClass",
      label: "Asset Class",
      format: VALUE_FORMATS.STRING,
      dataGetter: makeMetadataGetter("composition.assetClass"),
      dataPath: `${prefix}.assetClass`,
      downloadOnly: true,
      onlyShowOnceInDownload: true,
    }, {
      id: "currency",
      label: "Currency",
      format: VALUE_FORMATS.STRING,
      dataGetter: makeMetadataGetter("composition.currency"),
      dataPath: `${prefix}.currency`,
      downloadOnly: true,
      onlyShowOnceInDownload: true,
    }, {
      id: "industry",
      label: "Industry",
      format: VALUE_FORMATS.STRING,
      dataGetter: makeMetadataGetter("composition.industry"),
      dataPath: `${prefix}.industry`,
      downloadOnly: true,
      onlyShowOnceInDownload: true,
    },
  ]
}

export function getNMVTooltip(values, instrumentType) {
  if (!values) {
    return null
  }
  const suffix = INSTRUMENTS[instrumentType].metricsSuffix
  const gmvDataPath = makeDataPath("composition.gmv", suffix)
  const referenceEquityDataPath = makeDataPath("composition.referenceEquity", suffix)
  const gmv = get(values, gmvDataPath)
  const referenceEquity = get(values, referenceEquityDataPath)
  if (!gmv && !referenceEquity) {
    return null
  }
  return getNMVTooltipComponent(gmv, referenceEquity)
}

export function getDataGetter(metricDefinition, instrument, instrumentColumns) {
  if (metricDefinition.dataGetter) {
    return metricDefinition.dataGetter
  }
  if (INSTRUMENTS[instrument].hasDataGetter) {
    return makeDataGetter(metricDefinition.dataPath, instrumentColumns)
  }
  return null
}

export function getTooltipLabel(instrumentType, metric, instrumentsWithMetadata, isCompareWeightStrategy = false) {
  if (metric.tooltipInstrumentOverride) {
    return metric.tooltipInstrumentOverride
  }
  if (metric.hideInstrumentName) {
    return metric.label
  }

  if (isCompareWeightStrategy) {
    if (instrumentType === "delta") return "Sizing"
    if (instrumentType === "comparison") return "Selection"
  }
  const label = instrumentType === "delta" ? DELTA_SYMBOL : instrumentsWithMetadata?.[instrumentType]?.name
  return label
}

export function makeTooltipText(instrumentType, metric, instrumentsWithMetadata, isCompareWeightStrategy = false) {
  if (typeof metric.tooltipText === "function") {
    return metric.tooltipText(instrumentType, metric, instrumentsWithMetadata)
  }

  const label = getTooltipLabel(instrumentType, metric, instrumentsWithMetadata, isCompareWeightStrategy)
  if (label === metric.label) {
    return <div><CurrencyScaledLabel label={label} /></div>
  }
  return <div>
    {label}
    <br />
    <CurrencyScaledLabel label={metric.label} />
  </div>
}

export function getFilteredTriplicatedColumns(instrumentColumns, metricDefinition) {
  if (metricDefinition.filterInstrumentColumns) {
    return metricDefinition.filterInstrumentColumns(instrumentColumns)
  }

  return instrumentColumns
}

export function makeTooltipContents(metric, options = {}) {
  if (!metric.tooltipContents) {
    return null
  }
  if (typeof metric.tooltipContents === "function") {
    return metric.tooltipContents(options)
  }
  return metric.tooltipContents
}

export function makeDownloadLabel(instrumentsWithMetadata, instrumentType, metric, isEndVsStartDateComparison, isCompareWeightStrategy = false) {
  if (isCompareWeightStrategy) {
    if (instrumentType === "comparison") {
      return `Selection - ${metric.label}`
    }
    if (instrumentType === "delta") {
      return `Sizing - ${metric.label}`
    }
  }

  const isOptimization = instrumentsWithMetadata?.[instrumentType]?.isOptimization ?? false

  const instrumentTypeCharacter = getInstrumentTypeCharacter(instrumentType, isEndVsStartDateComparison, isCompareWeightStrategy, isOptimization)
  const instrument = instrumentsWithMetadata?.[instrumentType]
  if (instrument) {
    if (instrumentTypeCharacter) {
      return `${instrumentTypeCharacter}: ${instrument.name} - ${metric.label}`
    }
    return `${instrument.name} - ${metric.label}`
  }
  if (instrumentType === "delta") {
    return `${DELTA_SYMBOL} - ${metric.label}`
  }
  return metric.label
}

/**
 * Excludes asset metrics and beta metrics (they have unique categories with no overlap with other types).
 * Uses tooltipMetricType if explicitly set (e.g. "Intraday Standalone Performance 1W"),
 * otherwise derives a human-readable label from the dataPath prefix (e.g. "risk" → "Risk").
 */
function buildTooltipParts(dataPath, instrumentType, metric, instrumentsWithMetadata) {
  if (!dataPath) {
    return null
  }
  if (ASSET_METRIC_IDS.includes(metric.id) || metric.category === "beta") {
    return null
  }
  const metricType = metric.tooltipMetricType
    ?? dataPath.split(".")[0].replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (str) => str.toUpperCase())
  const label = metric.hideInstrumentLabel
    ? null
    : getTooltipLabel(instrumentType, metric, instrumentsWithMetadata)
  return { metricType, label }
}

export function makeTooltipExtendedText(dataPath, instrumentType, metric, instrumentsWithMetadata) {
  const parts = buildTooltipParts(dataPath, instrumentType, metric, instrumentsWithMetadata)
  if (!parts) {
    return null
  }
  const { metricType, label } = parts
  return (
    <div>
      <CurrencyScaledLabel label={metric.label} />
      <br />
      {metricType}
      {label != null && (
        <>
          <br />
          {label}
        </>
      )}
    </div>
  )
}

/**
 * Intraday contribution / standalone performance columns only (not price).
 */
export function makeContributorIntradayPerformanceDelayTooltipText(metric, delayText, instrumentsWithMetadata) {
  const dataPath = metric.baseDataPath ?? metric.dataPath
  const instrumentType = metric.type
  if (!instrumentType) {
    return null
  }
  const parts = buildTooltipParts(dataPath, instrumentType, metric, instrumentsWithMetadata)
  if (!parts) {
    return null
  }
  const { metricType, label } = parts
  return (
    <div>
      {delayText}
      <br />
      {metricType}
      {label != null && (
        <>
          <br />
          {label}
        </>
      )}
    </div>
  )
}

/**
 * Orders metric metadata based on different modes
 *
 * @param {Array} metricMetadata - Array of metric objects
 * @param {string} columnOrder - Column order mode ("default", "custom", or magnitude-based like "delta", "marketValue")
 * @param {Object} values - Data values for magnitude-based ordering
 * @returns {Array} Ordered metric metadata array
 */
export function orderMetricMetadata(metricMetadata, columnOrder, values) {
  if (!columnOrder || columnOrder === "default") {
    return metricMetadata
  }

  if (!Array.isArray(metricMetadata)) {
    return []
  }

  if (!values) {
    return metricMetadata.map((metric, index) => ({ ...metric, metricIndex: index }))
  }

  const categorizedMetrics = groupBy(metricMetadata, metric => metric.category || "")
  return Object.values(categorizedMetrics).reduce((result, categoryMetrics) => {
    const groupedMetrics = groupBy(categoryMetrics, "baseDataPath")

    const groupsWithSortValues = Object.values(groupedMetrics).map(group => {
      // If the columnOrder is not found, we use the first metric in the group.
      // This is for cases like performance delta with a rebalanced instrument
      // Where we don't calculate the delta, so we order by the value we have.
      const targetMetric = group.find(metric => metric.type === columnOrder) || group[0]

      let sortValue = 0
      if (!targetMetric.ignoreMagnitudeOrdering) {
        const getter = (metric) => metric.dataGetter ? metric.dataGetter(values) : get(values, metric?.dataPath, 0)
        const value = getter(targetMetric) || 0
        sortValue = Math.abs(value)
      }

      return { group, sortValue }
    })

    const sortedGroups = groupsWithSortValues.toSorted((a, b) => b.sortValue - a.sortValue)

    let currentMetricIndex = 0
    const categoryResult = sortedGroups.reduce((acc, { group }) => {
      const groupWithIndices = group.map((metric, index) => ({
        ...metric,
        metricIndex: currentMetricIndex + index,
      }))
      currentMetricIndex += group.length
      return [...acc, ...groupWithIndices]
    }, [])

    return [...result, ...categoryResult]
  }, [])
}

export function getSharpeLabel({ isRfrAttributionEnabled = false, isActive = false } = {}) {
  if (!isRfrAttributionEnabled) {
    return "Risk-Adjusted Performance"
  }
  return isActive ? "Information Ratio" : "Sharpe"
}

export function mapDescriptorItems(items, format) {
  return items.map(item => ({
    id: item,
    label: camelCaseToSpace(item),
    category: "descriptors",
    format,
    singleFactor: true,
    prefix: "descriptors",
    dataPath: `descriptors.${item}`,
  }))
}
